import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 15;

const usePaymentStatus = (sessionId, enabled) => {
  const [res, setRes] = useState({ state: enabled ? "checking" : "cancelled", purpose: null });
  useEffect(() => {
    if (!enabled) return undefined;
    if (!sessionId) { setRes({ state: "error", purpose: null }); return undefined; }
    let stop = false;
    const poll = async (n) => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setRes({ state: "paid", purpose: data.purpose });
        if (["expired", "failed"].includes(data.payment_status)) return setRes({ state: "error", purpose: data.purpose });
      } catch (_) { /* riprova */ }
      if (n + 1 >= MAX_POLLS) return setRes((r) => ({ ...r, state: "timeout" }));
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { stop = true; };
  }, [sessionId, enabled]);
  return res;
};

const COPY = {
  checking: ["Stiamo confermando il pagamento…", "Ci vuole qualche secondo, non chiudere la pagina."],
  paid: ["Pagamento riuscito!", "Ora hai l'app completa: preventivi, fatture, DDT, cantieri e tutto il resto."],
  paid_subscription: ["Abbonamento attivo!", "Grazie! Puoi continuare a usare K Gestionale senza interruzioni."],
  cancelled: ["Pagamento annullato", "Nessun addebito. Puoi riprovare quando vuoi."],
  error: ["Pagamento non riuscito", "Nessun addebito confermato. Puoi riprovare quando vuoi."],
  timeout: ["Conferma in ritardo", "Il pagamento è in verifica: ricarica la pagina tra un minuto."],
};

// Pagina a sé (fuori dal menu): funziona anche se la prova è scaduta.
export default function PagamentoEsito({ cancelled = false }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { state, purpose } = usePaymentStatus(params.get("session_id"), !cancelled);
  useEffect(() => { if (state === "paid" && localStorage.getItem("token")) refresh(); }, [state, refresh]);
  const key = state === "paid" && purpose === "subscription" ? "paid_subscription" : state;
  const [title, text] = COPY[key];
  const back = params.get("from") === "abbonamento" || purpose === "subscription" ? "/abbonamento" : "/impostazioni";
  const Icon = state === "paid" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  const tone = state === "paid" ? "text-emerald-600" : state === "checking" ? "text-primary animate-spin" : "text-amber-600";
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center space-y-4 animate-in-up" data-testid="payment-result">
        <img src="/k-gestionale.png" alt="K Gestionale" className="h-10 w-10 rounded-xl mx-auto" />
        <Icon className={`w-12 h-12 mx-auto ${tone}`} />
        <h1 className="font-heading text-2xl font-extrabold" data-testid="payment-result-title">{title}</h1>
        <p className="text-sm text-muted-foreground" data-testid="payment-result-text">{text}</p>
        {state !== "checking" && (
          <Button onClick={() => navigate(state === "paid" ? "/" : back)} data-testid="payment-result-button">
            {state === "paid" ? "Vai alla Home" : "Torna indietro"}
          </Button>
        )}
      </div>
    </div>
  );
}
